import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RefreshCw, Ban, Phone, Lock } from 'lucide-react';
import moment from 'moment';
import { getMarketSignals, getMarketSignalContacts } from '../../../api/dashboard';
import ContactLink from '../../contacts/components/ContactLink';
import RelistingAssignButton from './RelistingAssignButton';
import RelistingFindBuyersButton from './RelistingFindBuyersButton';
import RelistingNotes, { RelistingNoteButton } from './RelistingNotes';
import RelistingOwnerContacts from './RelistingOwnerContacts';
import RelistingOwnerEditModal from './RelistingOwnerEditModal';
import { hasSuperAdminAccess } from '../../../utils/roleAccess';

export const RELISTING_PAGE = '/prospecting/relisting-opportunities';

/**
 * "Market Signal Alerts, Powered by Homer's data feed".
 *
 * The panel from Christian's Growth OS mockup (Monday 12573204628): the CRM
 * tells agents who to call and when, from market data rather than manual
 * prospecting. The Homer lists are all read from the backend's nightly Homer event
 * cache (never a live Homer call on page load):
 *
 *   Sale-Cycle Watch         owners nearing their street's average hold period.
 *                            Not available: needs ownership/title history that
 *                            Homer does not hold. Shown as a locked row with
 *                            the reason, so nobody wonders where the data went.
 *   New Listing Alerts       a listing that just went live AND matches an
 *                            appraisal the agent is chasing (suburb, type,
 *                            bedrooms, price). Only the agent's own appraisals,
 *                            owners and contacts (Christian, 12 Oct, Monday
 *                            13202980753). "Notify list" expands the people.
 *   Re-listing Opportunities competitor listings withdrawn and not back on the
 *                            market, or 60+ days on market. "Add Lead" puts the
 *                            owner on Prospects under New (CRM-2026-00114);
 *                            "View All" opens the full searchable page.
 *                            Sits top left, above Sale-Cycle Watch (12 Oct).
 *   New Developments         Christian's 4th tile (1 Oct). Fed by the BCI
 *                            (Hubexo) projects API (Monday 12899126094): the
 *                            weekly sync's open projects in the tracked
 *                            suburbs, with the developer behind each. Locked
 *                            until BCI_CLIENT_ID is set on the backend.
 *
 * Listing expiry dates are not in Homer's data (private agency-agreement
 * terms), so days on market, price cuts and withdrawals are the proxies.
 */

const DAY_OPTIONS = [
  { key: 7, label: '7d' },
  { key: 14, label: '14d' },
  { key: 30, label: '30d' },
];

const PREVIEW_COUNT = 3;

export const money = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  const v = Number(n);
  // parseFloat drops trailing decimal zeros only; the old /\.?0+$/ also ate the
  // zero in "10", so $10M showed as "$1M" (found 23 Oct 2026 on a BCI project).
  if (v >= 1000000) return `$${parseFloat((v / 1000000).toFixed(2))}M`;
  if (v >= 1000) return `$${Math.round(v / 1000)}K`;
  return `$${v}`;
};

const typeLabel = (t) => {
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const specLine = (row) => {
  const parts = [];
  if (row.bedrooms) parts.push(`${row.bedrooms} bed`);
  if (row.bathrooms) parts.push(`${row.bathrooms} bath`);
  if (row.car_spaces) parts.push(`${row.car_spaces} car`);
  if (row.property_type) parts.push(typeLabel(row.property_type));
  if (row.land_size) parts.push(`${Math.round(row.land_size)}m²`);
  return parts.join(' · ');
};

const listingUrl = (row) => (row.listing_urls && row.listing_urls[0]) || row.listing_url || null;

const ContactRow = ({ c }) => (
  <div className="flex items-center justify-between gap-2 py-1 border-b border-gray-50 last:border-b-0">
    <div className="min-w-0">
      <ContactLink
        identity={{ contactId: c.id, email: c.email, name: c.name }}
        className="text-[12px] font-semibold text-teal-700 hover:underline truncate"
        returnTo="/dashboard"
        returnLabel="Dashboard"
      >
        {c.name}
      </ContactLink>
      <div className="text-[10px] text-gray-500 truncate">
        {c.address || 'No address on file'}
        {c.contact_type && c.contact_type !== 'general' ? ` · ${typeLabel(c.contact_type)}` : ''}
      </div>
    </div>
    {c.phone && (
      <a
        href={`tel:${c.phone.replace(/\s+/g, '')}`}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-teal-600 whitespace-nowrap"
      >
        <Phone className="w-3 h-3" /> {c.phone}
      </a>
    )}
  </div>
);

// One of the agent's own appraisals that a new listing matches, with the owner.
const MatchRow = ({ m }) => (
  <div className="flex items-center justify-between gap-2 py-1 border-b border-teal-100/60 last:border-b-0">
    <div className="min-w-0">
      <Link
        to={`/properties/${m.property_id}`}
        className="text-[12px] font-semibold text-teal-700 hover:underline truncate block"
      >
        {m.owner_name || 'Owner not recorded'}
      </Link>
      <div className="text-[10px] text-gray-500 truncate">
        {m.address}
        {m.beds ? ` · ${m.beds} bed` : ''}
        {m.price ? ` · Appraised ${money(m.price)}` : ''}
        {m.agent_name ? ` · ${m.agent_name}` : ''}
      </div>
    </div>
    {m.owner_contact && (
      <a
        href={`tel:${m.owner_contact.replace(/\s+/g, '')}`}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-teal-600 whitespace-nowrap"
      >
        <Phone className="w-3 h-3" /> {m.owner_contact}
      </a>
    )}
  </div>
);

const NotifyList = ({ listingId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMarketSignalContacts(listingId)
      .then((res) => alive && setData(res.data))
      .catch((err) => {
        console.error('Error loading notify list:', err);
        if (alive) setData(null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [listingId]);

  if (loading) return <div className="text-[11px] text-gray-400 py-2">Loading contacts…</div>;
  if (!data) return <div className="text-[11px] text-red-500 py-2">Could not load the contact list.</div>;

  const same = data.same_street || { count: 0, contacts: [] };
  const similar = data.similar_in_suburb || { count: 0, contacts: [] };
  const matches = data.matches || [];
  return (
    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
      {matches.length > 0 && (
        <div className="bg-teal-50/60 rounded-lg p-2.5 md:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-teal-700 mb-1">
            Your appraisals this matches · {matches.length}
          </div>
          {matches.map((m) => (
            <MatchRow key={`m-${m.property_id}`} m={m} />
          ))}
        </div>
      )}
      <div className="bg-gray-50 rounded-lg p-2.5">
        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1">
          Same street · {same.count}
        </div>
        {same.contacts.length === 0 ? (
          <div className="text-[11px] text-gray-400">Nobody on {data.street} in the CRM yet.</div>
        ) : (
          same.contacts.map((c) => <ContactRow key={`s-${c.id}`} c={c} />)
        )}
      </div>
      <div className="bg-gray-50 rounded-lg p-2.5">
        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1">
          Owners in {data.suburb} · {similar.count}
        </div>
        {similar.contacts.length === 0 ? (
          <div className="text-[11px] text-gray-400">No seller, landlord or investor contacts in {data.suburb}.</div>
        ) : (
          similar.contacts.map((c) => <ContactRow key={`o-${c.id}`} c={c} />)
        )}
      </div>
    </div>
  );
};


/* Card shell: white tile with title, subtitle, "View all" on the right and a
   divider under the header, as in Christian's 1 Oct mockup. */
const SignalCard = ({ title, subtitle, hot, count, showAll, onToggle, viewAll, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col min-w-0">
    <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-gray-900 truncate">{title}</h3>
          {hot && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-50 text-red-500 tracking-wide">
              HOT
            </span>
          )}
        </div>
        {subtitle && <p className="text-[11px] text-gray-500 mt-0.5 truncate">{subtitle}</p>}
      </div>
      {viewAll}
      {!viewAll && typeof count === 'number' && count > PREVIEW_COUNT && (
        <button
          onClick={onToggle}
          className="text-[12px] font-semibold text-teal-600 hover:underline whitespace-nowrap"
        >
          {showAll ? 'Show less' : `View all ${count}`}
        </button>
      )}
    </div>
    <div className="flex-1">{children}</div>
  </div>
);

// Round badge on the left of every row (NEW, 68d, WD...).
const Bubble = ({ text, cls }) => (
  <span
    className={`w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 ${cls}`}
  >
    {text}
  </span>
);

const ACTION_CLS = 'text-[12px] font-semibold text-teal-600 hover:underline whitespace-nowrap';

const RowShell = ({ bubble, title, lines, action, children }) => (
  <div className="py-3 border-b border-gray-100 last:border-b-0">
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {bubble}
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-gray-900 truncate">{title}</div>
          {lines.filter(Boolean).map((l, i) => (
            <div key={i} className={`text-[11px] truncate ${i === 0 ? 'text-gray-500' : 'text-gray-400'}`}>
              {l}
            </div>
          ))}
        </div>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
    {children}
  </div>
);

const NewListingRow = ({ row, expanded, onToggle }) => {
  const url = listingUrl(row);
  const guide = money(row.current_guide);
  const bubble = row.is_collings
    ? { text: 'OURS', cls: 'bg-teal-500' }
    : row.relisted
    ? { text: 'RELIST', cls: 'bg-amber-400 !text-[8px]' }
    : { text: 'NEW', cls: 'bg-green-500' };
  const extra = [row.agency_name || 'Agency unknown', specLine(row), guide ? `Guide ${guide}` : '']
    .filter(Boolean)
    .join(' · ');
  const matches = row.matches || [];
  const first = matches[0];
  const matchText = first
    ? `Matches your appraisal at ${first.address}${first.owner_name ? ` (${first.owner_name})` : ''}${
        matches.length > 1 ? ` + ${matches.length - 1} more` : ''
      }`
    : `${row.same_street_count || 0} same-street owners, ${row.similar_owner_count || 0} owner contacts in ${
        row.suburb
      } to notify`;
  return (
    <RowShell
      bubble={<Bubble {...bubble} />}
      title={row.address}
      lines={[
        `Listed ${moment(row.observed_at).fromNow()} · ${matchText}`,
        <>
          {extra}
          {url && (
            <>
              {' · '}
              <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 hover:underline">
                View listing
              </a>
            </>
          )}
        </>,
      ]}
      action={
        <button onClick={onToggle} className={ACTION_CLS}>
          {expanded ? 'Hide list ↑' : 'Notify list →'}
        </button>
      }
    >
      {expanded && <NotifyList listingId={row.listing_id} />}
    </RowShell>
  );
};

export const RelistingRow = ({ row, staleDays, onAssigned }) => {
  const [notes, setNotes] = useState(row.notes || []);
  const [notesOpen, setNotesOpen] = useState(false);
  const [owners, setOwners] = useState(row.owner_contacts || []);
  const [editing, setEditing] = useState(false);
  // The database team's Edit (DOMA 13382334362): media / tech and super
  // admins only, never the agents. The server enforces the same.
  const canEdit = hasSuperAdminAccess(useSelector((state) => state.auth?.user)) && Boolean(row.campaign_id);
  const url = listingUrl(row);
  const dom = row.days_on_market;
  // One of Collings' own listings that expired or was withdrawn (Monday 13354971743, 30 Oct 2026).
  const ours = row.source === 'collings';
  const bubble =
    row.source === 'sheet'
      ? { text: 'EXP', cls: 'bg-purple-500' }
      : ours
      ? { text: 'OURS', cls: 'bg-teal-600 !text-[9px]' }
      : row.reason_kind === 'withdrawn'
      ? { text: 'WD', cls: 'bg-purple-500' }
      : dom && dom >= staleDays
      ? { text: `${dom}d`, cls: 'bg-red-400' }
      : { text: dom ? `${dom}d` : 'CUT', cls: 'bg-amber-400' };
  const from = money(row.last_price_from);
  const to = money(row.last_price_to);
  const cuts = row.price_change_count || 0;
  // From the office's expired listings sheet (Monday 13354971743): no agency or withdrawal date on file.
  const fromSheet = row.source === 'sheet';
  const main = [
    fromSheet ? '' : ours ? (row.agent_name ? `Listed by ${row.agent_name}` : '') : `Listed with ${row.agency_name || 'another agency'}`,
    row.reason,
    row.reason_kind !== 'price_cut' && cuts > 0 ? `${cuts} price ${cuts === 1 ? 'reduction' : 'reductions'}` : '',
    row.reason_kind === 'withdrawn' && dom ? `${dom} days on market` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const current = money(row.current_guide);
  const guide = from && to ? `Guide ${from} → ${to}` : current ? `Guide ${current}` : '';
  const when =
    row.reason_kind === 'withdrawn' && row.withdrawn_date
      ? `Withdrawn ${moment(row.withdrawn_date).fromNow()}`
      : fromSheet
      ? ''
      : `Updated ${moment(row.observed_at).fromNow()}`;
  return (
    <RowShell
      bubble={<Bubble {...bubble} />}
      title={row.address}
      lines={[
        main,
        <>
          {[specLine(row), guide, when].filter(Boolean).join(' · ')}
          {url && (
            <>
              {' · '}
              <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-teal-600 hover:underline">
                View listing
              </a>
            </>
          )}
          {row.crm_property_id && (
            <>
              {' · '}
              <Link to={`/properties/${row.crm_property_id}`} className="hover:text-teal-600 hover:underline">
                Open our listing
              </Link>
            </>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="ml-2 px-2 py-0.5 rounded bg-blue-600 text-white text-[10px] font-bold tracking-wide hover:bg-blue-700"
            >
              EDIT
            </button>
          )}
        </>,
      ]}
      action={
        // Rows from the nightly campaigns table can be assigned. "Monitor" is
        // gone (Christian, 12 Oct): the action is to take the property on.
        row.kind ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <RelistingNoteButton count={notes.length} open={notesOpen} onToggle={() => setNotesOpen((o) => !o)} />
            <RelistingFindBuyersButton row={row} />
            <RelistingAssignButton row={row} onAssigned={onAssigned} />
          </div>
        ) : url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className={ACTION_CLS}>
            Prep pitch →
          </a>
        ) : null
      }
    >
      <RelistingOwnerContacts contacts={owners} people={row.crm_people} heldBy={row.held_by} />
      {editing && (
        <RelistingOwnerEditModal row={row} contacts={owners} onClose={() => setEditing(false)} onSaved={setOwners} />
      )}
      {row.kind && (
        <RelistingNotes
          campaignId={row.campaign_id || row.listing_id}
          notes={notes}
          open={notesOpen}
          onAdded={(n) => setNotes((prev) => [...prev, n])}
          onClose={() => setNotesOpen(false)}
        />
      )}
    </RowShell>
  );
};

// Badge from BCI's stage / status wording: where the project is up to.
const stageBubble = (row) => {
  const s = `${row.stage || ''} ${row.status || ''}`.toLowerCase();
  if (s.includes('abandon')) return { text: 'DROP', cls: 'bg-gray-400 !text-[9px]' };
  // BCI's stages: Concept, Design & Documentation, Pre-Construction, Construction.
  if (s.includes('pre-construct')) return { text: 'SOON', cls: 'bg-amber-500 !text-[9px]' };
  if (s.includes('construct')) return { text: 'BUILD', cls: 'bg-orange-400 !text-[8px]' };
  if (s.includes('tender')) return { text: 'TENDER', cls: 'bg-amber-400 !text-[8px]' };
  if (/(plan|design|concept|approv|feasib|da )/.test(s)) return { text: 'PLAN', cls: 'bg-sky-500 !text-[9px]' };
  return { text: 'NEW', cls: 'bg-indigo-500' };
};

export const NEW_DEVELOPMENTS_PAGE = '/prospecting/new-developments';

// onOpen makes the whole row a button that opens the project's detail panel
// (Christian, Monday 12899126094, 23 Oct 2026: "make every project clickable").
export const DevelopmentRow = ({ row, onOpen }) => {
  const value = money(row.value);
  const where = [row.address, row.suburb].filter(Boolean).join(', ');
  const specs = [
    row.category,
    row.residential_units ? `${row.residential_units} units` : '',
    row.storeys ? `${row.storeys} storeys` : '',
    value ? `Value ${value}` : '',
    row.construction_start ? `Starts ${moment(row.construction_start).format('MMM YYYY')}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const role = row.developer_role && row.developer_role.toLowerCase() !== 'developer' ? row.developer_role : 'Developer';
  const shell = (
    <RowShell
      bubble={<Bubble {...stageBubble(row)} />}
      title={row.name}
      lines={[
        [where, row.stage, row.status].filter(Boolean).join(' · '),
        specs,
        row.developer_name ? `${role}: ${row.developer_name}${row.developer_phone ? ` · ${row.developer_phone}` : ''}` : '',
      ]}
      action={
        row.developer_phone ? (
          <a
            href={`tel:${row.developer_phone.replace(/\s+/g, '')}`}
            className={ACTION_CLS}
            onClick={(e) => e.stopPropagation()}
          >
            Call developer →
          </a>
        ) : null
      }
    />
  );
  if (!onOpen) return shell;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open ${row.name}`}
      onClick={() => onOpen(row)}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpen(row);
        }
      }}
      className="-mx-2 px-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55C5D0]"
    >
      {shell}
    </div>
  );
};

// Shown in a tile whose data source is not switched on yet.
const LockedState = ({ title, children }) => (
  <div className="mt-3 border border-dashed border-gray-200 rounded-xl p-3 bg-gray-50">
    <div className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-600 mb-1">
      <Lock className="w-3.5 h-3.5" /> {title}
    </div>
    <p className="text-[11px] text-gray-500 leading-relaxed">{children}</p>
  </div>
);

const EmptyState = ({ children }) => (
  <div className="py-8 text-center text-gray-500 text-[12px] flex flex-col items-center gap-1">
    <Ban className="w-4 h-4 text-gray-300" />
    {children}
  </div>
);

const Loading = () => <div className="py-8 text-center text-gray-400 text-sm">Loading…</div>;

const MarketSignalAlertsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [showAllNew, setShowAllNew] = useState(false);
  const navigate = useNavigate();
  const [expandedListing, setExpandedListing] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getMarketSignals({ days, relist_days: 30, limit: 30 });
      setData(res.data);
    } catch (err) {
      console.error('Error loading market signals:', err);
      setError('Could not load market signals.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const status = data?.status || {};
  const newListings = data?.new_listings || [];
  const relisting = data?.relisting || [];
  const dropRelisting = (row) =>
    setData((d) =>
      d
        ? {
            ...d,
            relisting: (d.relisting || []).filter((r) => r.listing_id !== row.listing_id),
            relisting_total: Math.max(0, (d.relisting_total || 1) - 1),
          }
        : d,
    );
  const saleCycle = data?.sale_cycle || { available: false, items: [] };
  const developments = data?.developments || { configured: false, items: [] };
  const devItems = developments.items || [];
  const staleDays = status.stale_days || 60;
  const noEventsYet = !loading && data && status.event_count === 0;
  const firstLoad = loading && !data;

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2.5 px-0.5 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-sm sm:text-base font-bold text-gray-800">Market Signal Alerts</h2>
          <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-gray-900 text-white">
            Powered by Homer's data feed
          </span>
          {status.last_synced_at && (
            <span className="text-[11px] text-gray-400">
              Updated {moment(status.last_synced_at).fromNow()} · {status.suburbs_tracked} suburbs
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {DAY_OPTIONS.map((d) => (
            <button
              key={d.key}
              onClick={() => setDays(d.key)}
              className={`px-2.5 py-1 text-[11px] rounded-md font-medium transition-colors ${
                days === d.key ? 'bg-teal-50 text-teal-600' : 'text-gray-500 hover:bg-gray-100'
              }`}
              title={`New listings in the last ${d.key} days`}
            >
              {d.label}
            </button>
          ))}
          <button onClick={load} className="p-1.5 text-gray-400 hover:text-teal-600 transition-colors" title="Refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-white rounded-xl border border-gray-200 py-6 text-center text-sm text-red-500">{error}</div>
      )}

      {!error && noEventsYet && (
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-[11px] text-amber-800">
          {status.configured
            ? 'Waiting for the first Homer events sync. Alerts fill in after the nightly run (or `homer_events_sync`).'
            : 'Homer API key is not configured on the backend, so there is no market data to show yet.'}
        </div>
      )}

      {!error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Re-listing Opportunities sits top left, Sale-Cycle Watch under it
              (Christian, 12 Oct, Monday 13202980753: "Re-listing opportunities
              need to be at the top"). */}
          <SignalCard
            title="Re-listing Opportunities"
            subtitle={`Competitor listings withdrawn or ${staleDays}+ days on market`}
            hot={relisting.length > 0}
            viewAll={
              <button
                onClick={() => navigate(RELISTING_PAGE)}
                className="text-[12px] font-semibold text-teal-600 hover:underline whitespace-nowrap"
              >
                View All
              </button>
            }
          >
            {firstLoad ? (
              <Loading />
            ) : relisting.length === 0 ? (
              <EmptyState>No stalling competitor listings right now.</EmptyState>
            ) : (
              relisting.slice(0, PREVIEW_COUNT).map((row) => (
                <RelistingRow key={row.listing_id} row={row} staleDays={staleDays} onAssigned={dropRelisting} />
              ))
            )}
          </SignalCard>

          <SignalCard
            title="New Listing Alerts"
            subtitle="Just went live, matching properties you are chasing to list"
            count={newListings.length}
            showAll={showAllNew}
            onToggle={() => setShowAllNew((s) => !s)}
          >
            <div className={showAllNew ? 'max-h-[520px] overflow-y-auto no-scrollbar pr-1' : ''}>
              {firstLoad ? (
                <Loading />
              ) : newListings.length === 0 ? (
                <EmptyState>
                  No new listings in the last {days} days match the appraisals you are chasing (same suburb, type,
                  bedrooms and price).
                </EmptyState>
              ) : (
                (showAllNew ? newListings : newListings.slice(0, PREVIEW_COUNT)).map((row) => (
                  <NewListingRow
                    key={row.listing_id}
                    row={row}
                    expanded={expandedListing === row.listing_id}
                    onToggle={() =>
                      setExpandedListing((cur) => (cur === row.listing_id ? null : row.listing_id))
                    }
                  />
                ))
              )}
            </div>
          </SignalCard>

          {/* Sale-Cycle Watch: locked until a title-data source exists */}
          <SignalCard title="Sale-Cycle Watch" subtitle="Street avg. hold period, 12mo lead time">
            {saleCycle.available && saleCycle.items?.length ? (
              saleCycle.items.map((item) => (
                <RowShell
                  key={item.id || item.address}
                  bubble={<Bubble text={item.badge || '•'} cls="bg-orange-400" />}
                  title={item.address}
                  lines={[item.detail]}
                />
              ))
            ) : (
              <LockedState title="Not available yet">
                {saleCycle.reason || 'Needs ownership history that is not in the Homer feed.'}
              </LockedState>
            )}
          </SignalCard>

          {/* New Developments: Christian's 4th tile (1 Oct), fed by the BCI
              (Hubexo) projects API (Monday 12899126094). Locked until the
              backend has the BCI login. */}
          {/* "View All" opens the New Developments page, like Re-listing
              (Christian, 23 Oct 2026: "view all rather then view all 30"). */}
          <SignalCard
            title="New Developments"
            subtitle="Upcoming projects in the patch, powered by BCI"
            viewAll={
              developments.configured ? (
                <button
                  onClick={() => navigate(NEW_DEVELOPMENTS_PAGE)}
                  className="text-[12px] font-semibold text-teal-600 hover:underline whitespace-nowrap"
                >
                  View All
                </button>
              ) : null
            }
          >
            {firstLoad ? (
              <Loading />
            ) : !developments.configured ? (
              <LockedState title="Coming soon">
                New development projects in your suburbs (planning approvals, construction starts and the
                developers behind them) will show here once BCI switches on our data access.
              </LockedState>
            ) : devItems.length === 0 ? (
              <EmptyState>
                {developments.last_synced_at
                  ? 'No open development projects in the tracked suburbs right now.'
                  : 'BCI is connected. Projects show here after the first weekly sync.'}
              </EmptyState>
            ) : (
              devItems.slice(0, PREVIEW_COUNT).map((row) => (
                <DevelopmentRow
                  key={row.id}
                  row={row}
                  onOpen={(r) => navigate(`${NEW_DEVELOPMENTS_PAGE}?project=${encodeURIComponent(r.id)}`)}
                />
              ))
            )}
          </SignalCard>
        </div>
      )}
    </div>
  );
};

export default MarketSignalAlertsPanel;
